import { createPortal } from 'react-dom';
// src/components/MeetingModal.tsx
// Универсальное модальное окно: создание / редактирование / просмотр встречи + комментарии
import React, { useState, useEffect } from 'react';
import {
  X, Calendar, Clock, MapPin, Link as LinkIcon, FileText, Bell,
  MessageSquare, Send, Trash2, Edit, Download, AlertCircle, CheckCircle2,
  XCircle,
} from 'lucide-react';
import { meetingsApi, Meeting, MeetingInput, MeetingComment } from '../api/meetings';
import { useToast } from '../contexts/ToastContext';

type Mode = 'edit' | 'view';

interface Props {
  candidateId: string;
  candidateName?: string;
  meeting?: Meeting | null;
  mode?: Mode;
  onClose: () => void;
  onSaved: (meeting: Meeting) => void;
  onEdit?: (meeting: Meeting) => void;
  onCancel?: (meeting: Meeting) => void;
  onComplete?: (meeting: Meeting) => void;
}

function toLocalInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function MeetingModal({
  candidateId, candidateName, meeting,
  mode: initialMode, onClose, onSaved, onEdit, onCancel, onComplete,
}: Props) {
  const { showToast } = useToast();

  // Определяем режим: если встречу смотрим — по умолчанию view, если создаём — edit
  const [mode, setMode] = useState<Mode>(initialMode || (meeting ? 'view' : 'edit'));
  const isView = mode === 'view';

  const now = new Date();
  now.setMinutes(0, 0, 0);
  now.setHours(now.getHours() + 1);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startsAt, setStartsAt] = useState(toLocalInput(now));
  const [durationMin, setDurationMin] = useState(60);
  const [location, setLocation] = useState('');
  const [meetingLink, setMeetingLink] = useState('');
  const [notes, setNotes] = useState('');

  // Напоминания (в минутах). Ключ — минуты, значение — включено
  const [reminders, setReminders] = useState<Record<number, boolean>>({
    5: false,
    30: true,
    60: false,
    120: false,
    1440: false,
  });

  const [saving, setSaving] = useState(false);

  // Комментарии
  const [comments, setComments] = useState<MeetingComment[]>([]);
  const [newCommentText, setNewCommentText] = useState('');
  const [sendingComment, setSendingComment] = useState(false);

  // Заполнение полей
  useEffect(() => {
    if (meeting) {
      setTitle(meeting.title);
      setDescription(meeting.description || '');
      setStartsAt(toLocalInput(new Date(meeting.startsAt)));
      const dur = Math.round((new Date(meeting.endsAt).getTime() - new Date(meeting.startsAt).getTime()) / 60000);
      setDurationMin(dur > 0 ? dur : 60);
      setLocation(meeting.location || '');
      setMeetingLink(meeting.meetingLink || '');
      setNotes(meeting.notes || '');
      const rem = new Set((meeting.reminders || []).map(r => r.minutes));
      setReminders({
        5: rem.has(5),
        30: rem.has(30),
        60: rem.has(60),
        120: rem.has(120),
        1440: rem.has(1440),
      });
      setComments(meeting.comments || []);
    } else {
      const candidateNameShort = candidateName ? ` с ${candidateName}` : '';
      setTitle(`Собеседование${candidateNameShort}`);
    }
  }, [meeting, candidateName, mode]);

  // Загрузка свежих комментариев (для view-режима)
  useEffect(() => {
    if (isView && meeting?._id) {
      meetingsApi.get(meeting._id).then(fresh => {
        setComments(fresh.comments || []);
      }).catch(() => {});
    }
  }, [isView, meeting?._id]);

  // ─── Сохранение (edit mode) ───
  const handleSubmit = async () => {
    if (!title.trim()) {
      showToast('Введите название встречи', 'error');
      return;
    }

    const startDate = new Date(startsAt);
    if (isNaN(startDate.getTime())) {
      showToast('Некорректная дата начала', 'error');
      return;
    }

    const endDate = new Date(startDate.getTime() + durationMin * 60000);

    const remindersList: { minutes: number; sent: boolean; channel: 'push' }[] = [];
    [5, 30, 60, 120, 1440].forEach(m => {
      if (reminders[m]) {
        remindersList.push({ minutes: m, sent: false, channel: 'push' });
      }
    });

    const payload: MeetingInput = {
      candidateId,
      title: title.trim(),
      description: description.trim(),
      startsAt: startDate.toISOString(),
      endsAt: endDate.toISOString(),
      location: location.trim(),
      meetingLink: meetingLink.trim(),
      notes: notes.trim(),
      reminders: remindersList,
    };

    setSaving(true);
    try {
      const saved = meeting
        ? await meetingsApi.update(meeting._id || meeting.id!, payload)
        : await meetingsApi.create(payload);
      showToast(meeting ? 'Встреча обновлена' : 'Встреча назначена', 'success');

      // 📥 Автоскачивание .ics
      try {
        const savedId = saved._id || saved.id;
        if (savedId) {
          const cname = (candidateName || 'meeting').replace(/\s+/g, '-');
          const dateStr = new Date(saved.startsAt).toISOString().slice(0, 10);
          setTimeout(() => {
            meetingsApi.downloadIcs(savedId, `meeting-${cname}-${dateStr}.ics`).catch(() => {});
          }, 250);
        }
      } catch {}

      onSaved(saved);
      onClose();
    } catch (e: any) {
      console.error(e);
      showToast(e?.response?.data?.error || 'Не удалось сохранить встречу', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ─── Скачать .ics ───
  const handleDownload = async () => {
    if (!meeting) return;
    try {
      const cname = (candidateName || 'meeting').replace(/\s+/g, '-');
      const dateStr = new Date(meeting.startsAt).toISOString().slice(0, 10);
      await meetingsApi.downloadIcs(meeting._id || meeting.id!, `meeting-${cname}-${dateStr}.ics`);
      showToast('Файл календаря скачан', 'success');
    } catch {
      showToast('Не удалось скачать .ics', 'error');
    }
  };

  // ─── Добавить комментарий ───
  const handleAddComment = async () => {
    if (!newCommentText.trim() || !meeting) return;
    setSendingComment(true);
    try {
      const id = meeting._id || meeting.id!;
      const resp = await meetingsApi.addComment(id, newCommentText.trim());
      setComments(resp.comments || []);
      setNewCommentText('');
      showToast('Комментарий добавлен', 'success');
    } catch (err) {
      showToast('Не удалось добавить комментарий', 'error');
    } finally {
      setSendingComment(false);
    }
  };

  // ─── Удалить комментарий ───
  const handleRemoveComment = async (commentId: string) => {
    if (!meeting || !commentId) return;
    try {
      const id = meeting._id || meeting.id!;
      const resp = await meetingsApi.removeComment(id, commentId);
      setComments(resp.comments || []);
      showToast('Комментарий удалён', 'success');
    } catch {
      showToast('Не удалось удалить комментарий', 'error');
    }
  };

  // ─── Переключиться в edit ───
  const handleSwitchToEdit = () => {
    // Всегда переключаем режим ВНУТРИ модалки — не закрываем её
    setMode('edit');
  };

  return (
    createPortal(
<div className="meeting-modal-container" data-meeting-modal style={styles.backdrop} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Calendar size={22} color="#6a11cb" />
            <h3 style={styles.title}>
              {isView ? 'Встреча' : (meeting ? 'Редактировать встречу' : 'Новая встреча')}
            </h3>
            {isView && meeting && (
              <StatusBadge status={meeting.status} />
            )}
          </div>
          <button onClick={onClose} style={styles.closeBtn}><X size={20} /></button>
        </div>

        <div style={styles.body}>
          {isView && meeting ? (
            <>
              {/* Название */}
              <div style={styles.viewField}>
                <div style={styles.viewLabel}>Название</div>
                <div style={styles.viewValue}>{meeting.title}</div>
              </div>

              {/* Дата и время */}
              <div style={styles.row}>
                <div style={{ ...styles.viewField, flex: 2 }}>
                  <div style={styles.viewLabel}><Clock size={13} /> Начало</div>
                  <div style={styles.viewValue}>{fmtDateTime(meeting.startsAt)}</div>
                </div>
                <div style={{ ...styles.viewField, flex: 1 }}>
                  <div style={styles.viewLabel}>Длительность</div>
                  <div style={styles.viewValue}>
                    {Math.round((new Date(meeting.endsAt).getTime() - new Date(meeting.startsAt).getTime()) / 60000)} мин
                  </div>
                </div>
              </div>

              {/* Место */}
              {meeting.location && (
                <div style={styles.viewField}>
                  <div style={styles.viewLabel}><MapPin size={13} /> Место</div>
                  <div style={styles.viewValue}>{meeting.location}</div>
                </div>
              )}

              {/* Ссылка */}
              {meeting.meetingLink && (
                <div style={styles.viewField}>
                  <div style={styles.viewLabel}><LinkIcon size={13} /> Ссылка</div>
                  <a href={meeting.meetingLink} target="_blank" rel="noreferrer" style={styles.link}>
                    {meeting.meetingLink}
                  </a>
                </div>
              )}

              {/* Кандидат */}
              {meeting.candidate && (
                <div style={styles.viewField}>
                  <div style={styles.viewLabel}>Кандидат</div>
                  <div style={styles.viewValue}>
                    {meeting.candidate.lastName} {meeting.candidate.firstName}
                    {meeting.candidate.email && <span style={{ color: '#64748b', marginLeft: 8, fontSize: 13 }}>{meeting.candidate.email}</span>}
                  </div>
                </div>
              )}

              {/* Описание */}
              {meeting.description && (
                <div style={styles.viewField}>
                  <div style={styles.viewLabel}><FileText size={13} /> Описание</div>
                  <div style={styles.descriptionBlock}>{meeting.description}</div>
                </div>
              )}

              {/* Причина отмены */}
              {meeting.status === 'cancelled' && (
                <div style={styles.cancelBlock}>
                  <div style={styles.cancelHeader}>
                    <XCircle size={16} /> Встреча отменена
                  </div>
                  {meeting.cancelReason ? (
                    <div style={styles.cancelReason}>
                      <span style={{ fontWeight: 600 }}>Причина:</span> {meeting.cancelReason}
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: '#991b1b' }}>Причина не указана</div>
                  )}
                </div>
              )}

              {/* Комментарии */}
              <div style={styles.commentsSection}>
                <div style={styles.commentsHeader}>
                  <MessageSquare size={14} />
                  <span>Комментарии ({comments.length})</span>
                </div>

                {comments.length === 0 ? (
                  <div style={styles.commentsEmpty}>Пока нет комментариев</div>
                ) : (
                  <div style={styles.commentsList}>
                    {comments.map((c, idx) => (
                      <div key={c._id || idx} style={styles.commentItem}>
                        <div style={styles.commentHead}>
                          <span style={styles.commentAuthor}>{c.author}</span>
                          <span style={styles.commentDate}>
                            {new Date(c.createdAt).toLocaleString('ru-RU', {
                              day: '2-digit', month: '2-digit', year: '2-digit',
                              hour: '2-digit', minute: '2-digit',
                            })}
                          </span>
                          {c._id && (
                            <button
                              onClick={() => handleRemoveComment(c._id!)}
                              style={styles.commentDeleteBtn}
                              title="Удалить"
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                        <div style={styles.commentText}>{c.text}</div>
                      </div>
                    ))}
                  </div>
                )}

                <div style={styles.commentInputRow}>
                  <textarea
                    value={newCommentText}
                    onChange={e => setNewCommentText(e.target.value)}
                    placeholder="Добавить комментарий..."
                    rows={2}
                    style={styles.commentInput}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                        handleAddComment();
                      }
                    }}
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={!newCommentText.trim() || sendingComment}
                    style={{
                      ...styles.commentSendBtn,
                      opacity: !newCommentText.trim() || sendingComment ? 0.5 : 1,
                    }}
                    title="Ctrl+Enter — отправить"
                  >
                    <Send size={16} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Название */}
              <label style={styles.label}>
                <span style={styles.labelText}>Название *</span>
                <input
                  type="text"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="Собеседование с кандидатом"
                  style={styles.input}
                  autoFocus
                />
              </label>

              {candidateName && (
                <div style={styles.candidateBox}>
                  <span style={styles.candidateLabel}>Кандидат:</span>
                  <span style={styles.candidateName}>{candidateName}</span>
                </div>
              )}

              <div style={styles.row}>
                <label style={{ ...styles.label, flex: 2 }}>
                  <span style={styles.labelText}><Clock size={14} /> Начало *</span>
                  <input
                    type="datetime-local"
                    value={startsAt}
                    onChange={e => setStartsAt(e.target.value)}
                    style={styles.input}
                  />
                </label>
                <label style={{ ...styles.label, flex: 1 }}>
                  <span style={styles.labelText}>Длительность</span>
                  <select
                    value={durationMin}
                    onChange={e => setDurationMin(Number(e.target.value))}
                    style={styles.input}
                  >
                    <option value={15}>15 мин</option>
                    <option value={30}>30 мин</option>
                    <option value={45}>45 мин</option>
                    <option value={60}>1 час</option>
                    <option value={90}>1.5 часа</option>
                    <option value={120}>2 часа</option>
                  </select>
                </label>
              </div>

              <label style={styles.label}>
                <span style={styles.labelText}><MapPin size={14} /> Место</span>
                <input
                  type="text"
                  value={location}
                  onChange={e => setLocation(e.target.value)}
                  placeholder="Офис, каб. 5 / Онлайн"
                  style={styles.input}
                />
              </label>

              <label style={styles.label}>
                <span style={styles.labelText}><LinkIcon size={14} /> Ссылка (Zoom/Meet/Telemost)</span>
                <input
                  type="text"
                  value={meetingLink}
                  onChange={e => setMeetingLink(e.target.value)}
                  placeholder="https://..."
                  style={styles.input}
                />
              </label>

              <label style={styles.label}>
                <span style={styles.labelText}><FileText size={14} /> Описание</span>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="О чём будем говорить..."
                  rows={3}
                  style={{ ...styles.input, resize: 'vertical', fontFamily: 'inherit' }}
                />
              </label>

              <div style={styles.remindersBox}>
                <div style={styles.remindersHeader}>
                  <Bell size={14} /> <span>Напомнить:</span>
                </div>
                <div style={styles.remindersGrid}>
                  {[
                    { m: 5,    label: 'за 5 мин' },
                    { m: 30,   label: 'за 30 мин' },
                    { m: 60,   label: 'за 1 час' },
                    { m: 120,  label: 'за 2 часа' },
                    { m: 1440, label: 'за 1 день' },
                  ].map(({ m, label }) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setReminders(prev => ({ ...prev, [m]: !prev[m] }))}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 10,
                        border: `1.5px solid ${reminders[m] ? '#8b5cf6' : '#cbd5e1'}`,
                        background: reminders[m]
                          ? 'linear-gradient(135deg, rgba(139,92,246,0.15), rgba(139,92,246,0.05))'
                          : '#fff',
                        color: reminders[m] ? '#6d28d9' : '#64748b',
                        fontSize: 12,
                        fontWeight: reminders[m] ? 700 : 500,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {reminders[m] && <span>🔔</span>}
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <label style={styles.label}>
                <span style={styles.labelText}>Внутренние заметки (не видны в календаре)</span>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Что подготовить..."
                  rows={2}
                  style={{ ...styles.input, resize: 'vertical', fontFamily: 'inherit' }}
                />
              </label>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          {isView && meeting ? (
            <>
              <button onClick={handleDownload} style={styles.btnGhost} title="Скачать .ics">
                <Download size={15} /> .ics
              </button>
              {meeting.status !== 'cancelled' && meeting.status !== 'completed' && onComplete && (
                <button
                  onClick={() => { onComplete(meeting); onClose(); }}
                  style={styles.btnSuccessGhost}
                  title="Отметить как состоявшуюся"
                >
                  <CheckCircle2 size={15} /> Состоялась
                </button>
              )}
              {meeting.status !== 'cancelled' && meeting.status !== 'completed' && onCancel && (
                <button
                  onClick={() => { onCancel(meeting); onClose(); }}
                  style={styles.btnDangerGhost}
                >
                  <X size={15} /> Отменить встречу
                </button>
              )}
              <div style={{ flex: 1 }} />
              <button onClick={handleSwitchToEdit} style={styles.btnPrimary}>
                <Edit size={15} /> Редактировать
              </button>
            </>
          ) : (
            <>
              <button onClick={onClose} style={styles.btnSecondary} disabled={saving}>
                Отмена
              </button>
              <button onClick={handleSubmit} style={styles.btnPrimary} disabled={saving}>
                {saving ? 'Сохранение...' : (meeting ? 'Сохранить' : 'Назначить встречу')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
      , document.body)
  );
}

// ─── Статус badge ───
function StatusBadge({ status }: { status: Meeting['status'] }) {
  const map: Record<string, { text: string; bg: string; color: string; icon: any }> = {
    scheduled: { text: 'Запланирована', bg: '#eff6ff', color: '#2563eb', icon: Calendar },
    confirmed: { text: 'Подтверждена', bg: '#ecfdf5', color: '#10b981', icon: CheckCircle2 },
    cancelled: { text: 'Отменена', bg: '#fef2f2', color: '#ef4444', icon: XCircle },
    completed: { text: 'Завершена', bg: '#f8fafc', color: '#64748b', icon: CheckCircle2 },
    no_show: { text: 'Не пришёл', bg: '#fff7ed', color: '#f59e0b', icon: AlertCircle },
  };
  const s = map[status] || map.scheduled;
  const Icon = s.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 10px', borderRadius: 12,
      background: s.bg, color: s.color,
      fontSize: 11, fontWeight: 700,
    }}>
      <Icon size={12} /> {s.text}
    </span>
  );
}

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed', inset: 0,
    background: 'rgba(15, 23, 42, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 2000, padding: 20,
  },
  modal: {
    background: '#fff', borderRadius: 20,
    width: '100%', maxWidth: 620,
    maxHeight: '92vh', overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 25px 60px rgba(15, 23, 42, 0.25)',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '18px 22px', borderBottom: '1px solid #e2e8f0',
  },
  title: { margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' },
  closeBtn: {
    background: 'transparent', border: 'none', cursor: 'pointer',
    padding: 6, borderRadius: 8, color: '#64748b',
    display: 'flex', alignItems: 'center',
  },
  body: {
    padding: '20px 22px', overflowY: 'auto',
    display: 'flex', flexDirection: 'column', gap: 14,
  },
  label: { display: 'flex', flexDirection: 'column', gap: 6 },
  labelText: {
    fontSize: 13, fontWeight: 600, color: '#475569',
    display: 'flex', alignItems: 'center', gap: 6,
  },
  input: {
    width: '100%', padding: '10px 12px',
    border: '1px solid #cbd5e1', borderRadius: 10,
    fontSize: 14, color: '#0f172a', background: '#fff',
    outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  },
  row: { display: 'flex', gap: 12 },

  // ─── View поля ───
  viewField: { display: 'flex', flexDirection: 'column', gap: 4 },
  viewLabel: {
    fontSize: 12, fontWeight: 600, color: '#94a3b8',
    textTransform: 'uppercase', letterSpacing: 0.3,
    display: 'flex', alignItems: 'center', gap: 5,
  },
  viewValue: { fontSize: 15, color: '#0f172a', fontWeight: 500 },
  descriptionBlock: {
    fontSize: 14, color: '#334155', lineHeight: 1.5,
    background: '#f8fafc', padding: '10px 14px',
    borderRadius: 10, border: '1px solid #e2e8f0',
    whiteSpace: 'pre-wrap',
  },
  link: { color: '#2575fc', textDecoration: 'none', fontSize: 14, wordBreak: 'break-all' },

  candidateBox: {
    background: 'rgba(139, 92, 246, 0.08)',
    border: '1px solid rgba(139, 92, 246, 0.2)',
    borderRadius: 10, padding: '10px 14px',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  candidateLabel: { fontSize: 13, color: '#64748b' },
  candidateName: { fontSize: 14, fontWeight: 600, color: '#6d28d9' },

  // ─── Отмена ───
  cancelBlock: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 12, padding: '12px 16px',
  },
  cancelHeader: {
    display: 'flex', alignItems: 'center', gap: 8,
    fontSize: 14, fontWeight: 700, color: '#991b1b', marginBottom: 6,
  },
  cancelReason: { fontSize: 13, color: '#7f1d1d', lineHeight: 1.5 },

  remindersBox: {
    background: '#f8fafc', borderRadius: 10, padding: '12px 14px',
    display: 'flex', flexDirection: 'column', gap: 8,
  },
  remindersHeader: {
    display: 'flex', alignItems: 'center', gap: 6,
    fontSize: 13, fontWeight: 600, color: '#475569',
  },
  checkLabel: {
    display: 'flex', alignItems: 'center', gap: 8,
    fontSize: 13, color: '#334155', cursor: 'pointer',
  },

  // ─── Комментарии ───
  commentsSection: {
    borderTop: '1px solid #e2e8f0',
    paddingTop: 16, marginTop: 4,
    display: 'flex', flexDirection: 'column', gap: 10,
  },
  commentsHeader: {
    display: 'flex', alignItems: 'center', gap: 6,
    fontSize: 13, fontWeight: 700, color: '#475569',
  },
  commentsEmpty: {
    fontSize: 13, color: '#94a3b8', fontStyle: 'italic',
    padding: '12px 0', textAlign: 'center',
  },
  commentsList: {
    display: 'flex', flexDirection: 'column', gap: 8,
    maxHeight: 220, overflowY: 'auto', paddingRight: 4,
  },
  commentItem: {
    background: '#f8fafc', border: '1px solid #e2e8f0',
    borderRadius: 10, padding: '10px 12px',
  },
  commentHead: {
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4,
  },
  commentAuthor: { fontSize: 12, fontWeight: 700, color: '#6d28d9' },
  commentDate: { fontSize: 11, color: '#94a3b8' },
  commentDeleteBtn: {
    marginLeft: 'auto', background: 'transparent', border: 'none',
    cursor: 'pointer', color: '#ef4444', padding: 2, display: 'flex',
  },
  commentText: { fontSize: 13, color: '#0f172a', lineHeight: 1.5, whiteSpace: 'pre-wrap' },
  commentInputRow: {
    display: 'flex', gap: 8, alignItems: 'flex-end',
  },
  commentInput: {
    flex: 1, padding: '10px 12px',
    border: '1px solid #cbd5e1', borderRadius: 10,
    fontSize: 14, color: '#0f172a', background: '#fff',
    outline: 'none', resize: 'vertical', fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  commentSendBtn: {
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    border: 'none', color: '#fff',
    width: 40, height: 40, borderRadius: 10,
    cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },

  // ─── Footer ───
  footer: {
    display: 'flex', gap: 10, justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '16px 22px', borderTop: '1px solid #e2e8f0',
    background: '#f8fafc',
  },
  btnPrimary: {
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: '#fff', border: 'none',
    borderRadius: 10, padding: '10px 22px',
    fontSize: 14, fontWeight: 600, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 6,
  },
  btnSecondary: {
    background: '#fff', color: '#475569',
    border: '1px solid #cbd5e1',
    borderRadius: 10, padding: '10px 22px',
    fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
  btnGhost: {
    background: '#fff', color: '#2575fc',
    border: '1px solid rgba(37,117,252,0.3)',
    borderRadius: 10, padding: '9px 14px',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 5,
  },
  btnSuccessGhost: {
    background: '#fff', color: '#10b981',
    border: '1px solid rgba(16,185,129,0.3)',
    borderRadius: 10, padding: '9px 14px',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 5,
  },
  btnDangerGhost: {
    background: '#fff', color: '#ef4444',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: 10, padding: '9px 14px',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 5,
  },
};
